import {
  Button,
  createStyles,
  lighten,
  makeStyles,
  Theme,
  Tooltip,
} from '@material-ui/core'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { BoMItemType } from 'model/Project/BoMItemRow'
import React, { CSSProperties } from 'react'
import { useBomItemThumbnailImageSrc } from '../useBomItemThumbnailImageSrc'

type RowThumbnailButtonSizes = 'small' | 'medium' | 'large'

type BomItemThumbnailButtonProps = {
  rowId: string
  bomItemType: BoMItemType
  threeJSContainerStyle?: CSSProperties
  show2dModel?: boolean
  withCameraControls?: boolean
  showToolbox?: boolean
  size?: RowThumbnailButtonSizes
  showTooltip?: boolean
  pictureClassName?: string
  disableClick?: boolean
  operationPending?: boolean
}

const useStyles = (
  size?: RowThumbnailButtonSizes,
  disableClick?: boolean,
  operationPending?: boolean
) =>
  makeStyles((theme: Theme) => {
    let width = theme.spacing(6)

    if (size === 'small') width = theme.spacing(4)
    if (size === 'large') width = theme.spacing(10)

    return createStyles({
      imgSize: {
        width: width,
        height: width,
        flexShrink: 0,
        flexGrow: 0,
      },
      thumbnailSize: {
        width: theme.spacing(30),
      },
      imgButton: {
        padding: 0,
        zIndex: 0,
        backgroundColor: operationPending
          ? lighten(theme.palette.action.disabled, 0.825)
          : 'inherit',
        mixBlendMode: operationPending
          ? 'multiply'
          : theme?.BoMThumbnail?.mixBlendMode,
        minWidth: width,
      },
      thumbnailButton: {
        pointerEvents: disableClick === true ? 'none' : 'all',
      },
      thumbnail: {
        padding: `${theme.spacing(1)}px`,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
      },
    })
  })

const BomItemThumbnailButton = (props: BomItemThumbnailButtonProps) => {
  const cn = useStyles(props.size, props.disableClick, props.operationPending)()

  const { openDialog, closeDialog } = useDialogManager()
  const imageRef = React.useRef(undefined)

  const setDetailsOpen = (e) => {
    e.stopPropagation()

    openDialog('RowDetails', {
      rowId: props.rowId,
      open: true,
      onClose: () => closeDialog(),
      bomItemType: props.bomItemType,
    })
  }

  const { imageSrc, thumbnailFilename } = useBomItemThumbnailImageSrc(
    props.rowId,
    props.show2dModel
  )

  if (!thumbnailFilename) {
    return <div className={cn.imgSize} />
  }

  if (props.showTooltip) {
    return (
      <Tooltip
        placement={'left'}
        arrow
        title={
          <img loading="lazy" src={imageSrc} className={cn.thumbnailSize} />
        }
        classes={{
          tooltip: cn.thumbnail,
        }}
      >
        <Button
          onClick={(e) => setDetailsOpen(e)}
          disableFocusRipple
          disableTouchRipple
          disableRipple
          disableElevation
          className={`${props.pictureClassName} ${cn.imgButton}`}
        >
          <img
            src={imageSrc}
            loading="lazy"
            className={cn.imgSize}
            ref={imageRef}
          />
        </Button>
      </Tooltip>
    )
  }

  return (
    <Button
      onClick={(e) => setDetailsOpen(e)}
      disableFocusRipple
      disableTouchRipple
      disableRipple
      disableElevation
      disabled={props.disableClick}
      className={props.pictureClassName}
    >
      <img
        loading="lazy"
        src={imageSrc}
        className={cn.imgSize}
        ref={imageRef}
      />
    </Button>
  )
}

export { BomItemThumbnailButton as RowThumbnailButton }
